import {
  clearManageFlowBooking,
  deleteRefundOffer,
  getManageFlowBookingById,
  getManageFlowTravelAccountsMap,
  initiateExtrasRefund,
  initiateLegsRefund,
  initiateNonTripOffersRefund,
  resetChangeTripsSelection,
  resetRefundSelection,
  setChangeBookingFlow,
  setChangeTripsSelectedTripId,
  toggleRefundAncillary,
  toggleRefundLeg,
  toggleRefundLegAdmission,
  toggleRefundNonTripAdmission,
  type ChangeBookingFlow,
} from '@/features/manage/manageActions';
import type {
  AdmissionDTO,
  BookedAncillaryDTO,
  BookingDTO,
  RefundOffersDTO,
} from '@/types/dto/agent';
import type { TravelAccountsMap } from '@/types/travelpass';
import { orderBookingTripsBasedOnDepartureTime } from '@/utils/booking';
import { createReducer } from '@reduxjs/toolkit';

type ManageBookingState = {
  refund: {
    selectedLegAdmissions: { [legId: string]: Array<AdmissionDTO> };
    selectedNonTripAdmissions: { [admissionId: string]: AdmissionDTO };
    selectedAncillaries: { [ancillaryId: string]: BookedAncillaryDTO };
    details: RefundOffersDTO | null;
  };
  changeTrips: {
    outboundTripId: string | null;
    returnTripId: string | null;
    changeFlow: ChangeBookingFlow | null;
  };
  booking: BookingDTO | null;
  travelAccountsMap?: TravelAccountsMap;
};

const initialState: ManageBookingState = {
  refund: {
    selectedLegAdmissions: {},
    selectedNonTripAdmissions: {},
    selectedAncillaries: {},
    details: null,
  },
  changeTrips: {
    outboundTripId: null,
    returnTripId: null,
    changeFlow: null,
  },
  booking: null,
};

export const manageReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(toggleRefundLeg, (state, action) => {
      const { legId, admissions } = action.payload;
      if (admissions.length > 0) {
        state.refund.selectedLegAdmissions[legId] = admissions;
      } else {
        state.refund.selectedLegAdmissions = Object.fromEntries(
          Object.entries(state.refund.selectedLegAdmissions).filter(
            ([key]) => key !== legId
          )
        );
      }
    })
    .addCase(toggleRefundLegAdmission, (state, action) => {
      const { legId, admissions } = action.payload;
      if (admissions.length > 0) {
        state.refund.selectedLegAdmissions[legId] = admissions;
      } else {
        state.refund.selectedLegAdmissions = Object.fromEntries(
          Object.entries(state.refund.selectedLegAdmissions).filter(
            ([key]) => key !== legId
          )
        );
      }
    })
    .addCase(toggleRefundNonTripAdmission, (state, action) => {
      const admissionId = action.payload.id;

      if (!admissionId) {
        return;
      }

      if (state.refund.selectedNonTripAdmissions[admissionId]) {
        state.refund.selectedNonTripAdmissions = Object.fromEntries(
          Object.entries(state.refund.selectedNonTripAdmissions).filter(
            ([key]) => key !== admissionId
          )
        );
      } else {
        state.refund.selectedNonTripAdmissions[admissionId] = action.payload;
      }
    })
    .addCase(toggleRefundAncillary, (state, action) => {
      const ancillaryId = action.payload.id;

      if (!ancillaryId) {
        return;
      }

      if (state.refund.selectedAncillaries[ancillaryId]) {
        state.refund.selectedAncillaries = Object.fromEntries(
          Object.entries(state.refund.selectedAncillaries).filter(
            ([key]) => key !== ancillaryId
          )
        );
      } else {
        state.refund.selectedAncillaries[ancillaryId] = action.payload;
      }
    })
    .addCase(resetRefundSelection, (state) => {
      state.refund.selectedLegAdmissions = {};
      state.refund.selectedNonTripAdmissions = {};
      state.refund.selectedAncillaries = {};
      state.refund.details = null;
    })
    .addCase(initiateLegsRefund.fulfilled, (state, action) => {
      state.refund.details = action.payload;
    })
    .addCase(initiateExtrasRefund.fulfilled, (state, action) => {
      state.refund.details = action.payload;
    })
    .addCase(initiateNonTripOffersRefund.fulfilled, (state, action) => {
      state.refund.details = action.payload;
    })
    .addCase(deleteRefundOffer.fulfilled, (state) => {
      state.refund.details = null;
    })
    .addCase(setChangeBookingFlow, (state, action) => {
      state.changeTrips.changeFlow = action.payload;
    })
    .addCase(setChangeTripsSelectedTripId, (state, action) => {
      const { tripId, isOutbound } = action.payload;
      if (isOutbound) {
        state.changeTrips.outboundTripId = tripId;
      } else {
        state.changeTrips.returnTripId = tripId;
      }
    })
    .addCase(resetChangeTripsSelection, (state) => {
      state.changeTrips.outboundTripId = null;
      state.changeTrips.returnTripId = null;
      state.changeTrips.changeFlow = null;
    })
    .addCase(getManageFlowBookingById.fulfilled, (state, action) => {
      state.booking = orderBookingTripsBasedOnDepartureTime(action.payload);
    })
    .addCase(clearManageFlowBooking, (state) => {
      Object.assign(state, {
        ...initialState,
      });
    })
    .addCase(getManageFlowTravelAccountsMap.fulfilled, (state, action) => {
      state.travelAccountsMap = action.payload;
    });
});
